import { Controller, useFormContext, useWatch } from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import CustomDropdown from "./CustomDropdown";
import FieldError from "./FieldError";
import { JobFormValues, parseAmount } from "../schema/jobForm.schema";
import {
  COMPENSATION_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  CURRENCY_SYMBOLS,
  JOB_POST_DURATION_OPTIONS,
  JOB_TYPE_OPTIONS,
  POSITION_LEVEL_OPTIONS,
  WORKPLACE_OPTIONS,
} from "../schema/jobForm.options";
import {
  CLASSIFICATION_OPTIONS,
  getClassificationGroup,
  getClassificationSummary,
} from "@utils/classification";

const errorStyle = (hasError: boolean) => (hasError ? "border-orange-700" : "");

const Step2 = ({ activeStep }) => {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const [
    classification,
    discipline,
    compensationType,
    currency,
    salaryMin,
    salaryMax,
  ] = useWatch({
    control,
    name: [
      "classification",
      "discipline",
      "compensationType",
      "currency",
      "salaryMin",
      "salaryMax",
    ],
  });

  const disciplineOptions =
    getClassificationGroup(classification)?.subOptions ?? [];

  const payUnit = COMPENSATION_TYPE_OPTIONS.find(
    (option) => option.value === compensationType,
  )?.unit;
  const symbol = CURRENCY_SYMBOLS[currency] ?? "";
  const formatAmount = (value: string) =>
    value && !Number.isNaN(parseAmount(value))
      ? `${symbol}${parseAmount(value).toLocaleString()}`
      : "—";

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}{" "}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>
      {/* Matching context */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              01
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">Matching context</span>
              <span className="text-xss font-medium text-light-black">
                Establish the professional context before entering the public
                job title.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Required for matching
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1fr] gap-4 p-4">
          <div className="flex flex-col gap-3 pr-24">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Classification
              </label>
              <Controller
                control={control}
                name="classification"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={CLASSIFICATION_OPTIONS}
                      value={field.value}
                      onChange={(value) => {
                        if (value === field.value) return;
                        field.onChange(value);
                        // A discipline only belongs to its parent classification.
                        setValue("discipline", "", { shouldDirty: true });
                      }}
                      placeholder="Select a classification"
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
              <span className="text-xsss text-light-black font-medium">
                Choose the broad professional field this role belongs to.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job function or discipline
              </label>
              <Controller
                control={control}
                name="discipline"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={disciplineOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={
                        classification
                          ? "Select a job function"
                          : "Select a classification first"
                      }
                      disabled={!disciplineOptions.length}
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
              <span className="text-xsss text-light-black font-medium">
                Narrows the role context used for title and skill matching.
              </span>
            </div>
          </div>
          <div className="flex flex-col border-l-[3px] border-slate-600 bg-slate-50 px-5 justify-center gap-2">
            <span className="uppercase font-bold text-xss text-slate-600 tracking-wider">
              Why this comes first
            </span>
            <span className="text-xss font-medium text-light-black">
              A title can mean different things across industries. COMTRACKED
              keeps this classification and discipline attached to the job so
              later skill comparisons are made in the correct context.
            </span>
            {classification ? (
              <span className="font-semibold text-xss">
                {getClassificationSummary({
                  main: classification,
                  sub: discipline ? [discipline] : [],
                })}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Role and working arrangement */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              02
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">
                Role and working arrangement
              </span>
              <span className="text-xss font-medium text-light-black">
                Imported facts remain editable before the post is published.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Candidate-facing facts
          </div>
        </div>

        <div className="flex flex-col p-4 gap-3">
          <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job title
              </label>
              <CustomInputV2
                placeholder="e.g. Cyber Security Analyst"
                inputContainerStyle={errorStyle(!!errors.title)}
                {...register("title")}
              />
              <FieldError message={errors.title?.message} />
              <span className="text-xsss text-light-black font-medium">
                Use the recognisable public title; the matching context is
                stored separately.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Experience level
              </label>
              <Controller
                control={control}
                name="positionLevel"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={POSITION_LEVEL_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select level"
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Location
              </label>
              <CustomInputV2
                placeholder="e.g. London"
                inputContainerStyle={errorStyle(!!errors.location)}
                {...register("location")}
              />
              <FieldError message={errors.location?.message} />
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Workplace type
              </label>
              <Controller
                control={control}
                name="workPlaceOption"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={WORKPLACE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select workplace type"
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Employment type
              </label>
              <Controller
                control={control}
                name="jobType"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={JOB_TYPE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select employment type"
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job post duration
              </label>
              <Controller
                control={control}
                name="jobPostDurationDays"
                render={({ field, fieldState }) => (
                  <>
                    <CustomDropdown
                      options={JOB_POST_DURATION_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select duration"
                      hasError={!!fieldState.error}
                    />
                    <FieldError message={fieldState.error?.message} />
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5 w-72">
            <label className="text-xss font-bold text-slate-600">
              Qualification or eligibility
            </label>
            <CustomInputV2
              placeholder="Optional"
              {...register("qualificationEligibility")}
            />
          </div>
        </div>
      </div>

      {/* Compensation */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              03
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">Compensation </span>
              <span className="text-xss font-medium text-light-black">
                Describe pay in the unit candidates will actually compare.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Shown on the job post
          </div>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-slate-600">
              How is this role paid?
            </label>
            <Controller
              control={control}
              name="compensationType"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  {COMPENSATION_TYPE_OPTIONS.map((payType) => {
                    const isSelected = field.value === payType.value;
                    return (
                      <button
                        key={payType.value}
                        type="button"
                        onClick={() => field.onChange(payType.value)}
                        className={`flex items-center justify-center border rounded-md h-10 flex-1 text-xss font-bold ${isSelected ? "border-slate-600 bg-slate-600 text-white" : "text-slate-600"}`}
                      >
                        {payType.label}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>

          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5 w-32">
              <label className="text-xss font-bold text-slate-600">
                Currency
              </label>
              <Controller
                control={control}
                name="currency"
                render={({ field, fieldState }) => (
                  <CustomDropdown
                    options={CURRENCY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    hasError={!!fieldState.error}
                  />
                )}
              />
            </div>
            <div className="flex flex-col rounded-md border bg-slate-50 flex-1 p-2 gap-1">
              <span className="text-xss font-bold text-slate-600">
                Pay range
              </span>
              <div className="flex items-end justify-between gap-3">
                <div className="flex  flex-col gap-1.5 flex-1">
                  <label className="text-xss font-semibold text-gray-500">
                    FROM
                  </label>
                  <CustomInputV2
                    inputMode="decimal"
                    placeholder="65,000"
                    inputContainerStyle={errorStyle(!!errors.salaryMin)}
                    {...register("salaryMin")}
                  />
                </div>
                <span className="text-xss text-center mb-3">to</span>
                <div className="flex  flex-col gap-1.5 flex-1">
                  <label className="text-xss font-semibold text-gray-500">
                    TO
                  </label>
                  <CustomInputV2
                    inputMode="decimal"
                    placeholder="78,000"
                    inputContainerStyle={errorStyle(!!errors.salaryMax)}
                    {...register("salaryMax")}
                  />
                </div>
                <div className="font-bold text-xss text-light-black border-l pl-2 flex items-center h-10 w-14">
                  {payUnit}
                </div>
              </div>
              <FieldError
                message={errors.salaryMin?.message ?? errors.salaryMax?.message}
              />
            </div>
          </div>

          <div className="flex items-center justify-between border rounded-md px-3.5 py-3 gap-1.5 ">
            <label className="uppercase text-xsss font-bold text-light-black tracking-wider">
              candidate-facing pay
            </label>
            <span className="text-content-muted text-xss font-bold">
              {formatAmount(salaryMin)}–{formatAmount(salaryMax)} {payUnit}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step2;
